import { Injectable } from "@angular/core";
import PubNub from "pubnub";
import { LocalStorageKeys } from "@models/LocalStorageKeys";
import { environment } from "../../../../../../environments/environment";

export const config = {
    publishKey: environment.pubNubPublishKey,
    subscribeKey: environment.pubNubSubscribeKey,
};

export const MESSAGES = {
    EVICTION_TITLE: "Simultaneous sessions detected",
    EVICTION_MESSAGE: "The campaign is currently being edited by another user. To prevent content issues, " +
        "we do not support multi-editing. If you take over the editing, " +
        "the current user will be redirected away from the campaign editor.",
    TAKEOVER_NOTICE_TITLE: "Simultaneous sessions detected",
    TAKEOVER_NOTICE_MESSAGE: "Another user is taking over. Your work will be saved and you’ll be redirected.",
    TAKEOVER_CONFIRMED_TITLE: "You Now Have Control",
    TAKEOVER_CONFIRMED_MESSAGE: "Previous user has saved their changes. You can now edit this campaign.",
    ERROR_GENERIC: "Something went wrong. Please try again.",
} as const;

@Injectable({
    providedIn: "root",
})
export class PubNubService {
    private pubnub: PubNub;
    private presenceListeners: Array<{ presence: (presence: any) => void }> = [];

    constructor() {
        const contextString = localStorage.getItem(LocalStorageKeys.CONTEXT);
        const sessionId = localStorage.getItem(LocalStorageKeys.SESSION_ID);
        const user = contextString ? JSON.parse(contextString) : null;
        const userId = String(user?.id);
        const userIdWithSession = sessionId ? `${userId}${sessionId}` : userId;

        this.pubnub = new PubNub({
            heartbeatInterval: 120,
            publishKey: config.publishKey,
            subscribeKey: config.subscribeKey,
            userId: userIdWithSession,
        });
    }

    getUserId(): string {
        return this.pubnub.getUserId();
    }

    joinChannel(channel: string, callback: (userCount: any) => void): void {
        this.pubnub.subscribe({ channels: [channel], withPresence: true });

        this.pubnub.hereNow(
            { channels: [channel], includeUUIDs: true, includeState: true },
            (status, response) => {
                if (status.error) {
                    callback({ count: 0, occupants: [] });
                    return;
                }

                const channelData = response.channels[channel];
                const occupantsList = channelData?.occupants || [];
                const count = channelData?.occupants?.length || 0;

                callback({
                    count,
                    occupants: occupantsList,
                });
            }
        );
    }

    listen(callback: (message: any) => void): void {
        this.pubnub.addListener({
            message: (msg) => {
                callback(msg.message);
            },
        });
    }

    listenPresence(callback: (presence: any) => void): void {
        const listener = { presence: callback };
        this.pubnub.addListener(listener);
        this.presenceListeners.push(listener);
    }

    removeAllPresenceListeners(): void {
        this.presenceListeners.forEach((listener) => {
            this.pubnub.removeListener(listener);
        });
        this.presenceListeners = [];
    }

    sendMessage(channel: string, message: any): void {
        void this.pubnub.publish({ channel, message });
    }

    leaveChannel(channel: string): void {
        this.pubnub.unsubscribe({ channels: [channel] });
    }

    leaveAllChannels(): void {
        this.pubnub.unsubscribeAll();
    }
}
